<script lang="ts" setup>
import { onKeyStroke } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { codes } from "~/entities/phone-code";

const props = defineProps<{
  isOpened: boolean;
}>();

const emit = defineEmits<{
  send: [string];
  close: [];
}>();

const { t } = useI18n();
const phone = ref("");
const isDisabled = ref(true);
const isOpened = ref(props.isOpened);

const phoneCodes = codes.map((c) => c.code);

const checkPhone = () => {
  if (
    phone.value.length > 11 &&
    phoneCodes.some((p) => phone.value.includes(p))
  ) {
    isDisabled.value = false;
    return;
  }

  isDisabled.value = true;
};

watch(phone, () => {
  checkPhone();
});

const onSend = async () => {
  emit("send", phone.value);
};

const isError = computed(() => {
  if (
    phone.value.length > 4 &&
    !phoneCodes.some((p) => phone.value.includes(p))
  ) {
    return t("triggerBroadcast.modal.phoneNumberHint");
  }

  if (phone.value.length > 11 && isDisabled.value) {
    return t("triggerBroadcast.modal.phoneNumberHint");
  }

  return false;
});

watch(() => props.isOpened, (v: boolean) => {
  isOpened.value = v;
});
watch(isOpened, (isOpened: boolean) => {
  if (!isOpened) {
    phone.value = '';
    emit('close');
  }
});
onKeyStroke('Enter', onSend);
</script>

<template>
  <div class="content">
    <PDialog v-model="isOpened" :loading="false" width="x-small">
      <template #header>
        <div class="topbar">
          <div class="topbar-bar">
            <span></span>
            <div class="topbar-bar-title">
              {{ $t("triggerBroadcast.modal.testMessage") }}
            </div>
            <span class="topbar-bar-icon" @click="emit('close')">
              <IconCrossMd />
            </span>
          </div>
        </div>
      </template>
      <div class="main">
        <div class="main-wrapper">
          <div class="main-wrapper-text">
            {{ $t("triggerBroadcast.modal.sendMessage") }}
          </div>
          <PInput type="tel" :error="isError" text-size="medium" size="large"
            :label="$t('triggerBroadcast.modal.phoneNumber')" :hint="$t('triggerBroadcast.modal.phoneNumberHint')"
            mask="+#############" v-model="phone">
          </PInput>
        </div>
      </div>
      <template #footer>
        <div class="footer w-full">
          <div class="footer-bar w-full">
            <PButton class="w-full" @click="onSend" :disabled="isDisabled">{{
              $t("common.send")
              }}</PButton>
          </div>
        </div>
      </template>
    </PDialog>
  </div>
</template>

<style lang="scss" scoped>
.topbar {
  display: flex;
  padding: 0px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;

  background: var(--common-white);
  box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0.08) inset;

  &-bar {
    display: flex;
    height: 48px;
    padding: 8px 0px;
    align-items: center;
    gap: 12px;
    align-self: stretch;

    &-icon {
      display: flex;
      width: 20px;
      height: 20px;
      justify-content: center;
      align-items: center;

      cursor: pointer;
    }

    &-title {
      display: flex;
      height: 40px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      flex: 1 0 0;

      color: var(--common-black, #2b3238);
      text-align: center;

      /* Semibold/16 */
      font-size: 16px;
      font-family: Inter;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;
    }
  }
}

.main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding-top: 16px;
  padding-bottom: 16px;

  &-wrapper {
    display: flex;
    padding: 0px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;

    &-text {
      text-align: center;
      display: flex;
      align-items: flex-start;
      gap: 10px;
      align-self: stretch;

      color: var(--common-dark-water, #5d7285);
      font-size: 14px;
      font-family: Inter;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    }
  }
}

.footer {
  display: flex;
  height: 68px;
  padding: 8px 16px 16px 16px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  &-bar {
    display: flex;
    width: 328px;
    justify-content: center;
    align-items: center;
    gap: 12px;
  }
}
</style>
